import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/common/ui/select';
import { getAssessmentStatusLabel, getAssessmentStatusColor } from 'common/utils/assessmentStatusHelpers';
import { AssessmentPeriodRead } from '@/store/generated/portal/assessments';

const AssessmentPeriodListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const hexToRgba = (hex: string, opacity: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

const StatusBadge = styled.span<{ color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  background-color: ${props => hexToRgba(props.color, 0.1)};
  color: ${props => props.color};
  margin-left: 8px;
`;

const OptionContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

type AssessmentPeriodListWithStatusProps = {
  selectedAssessmentPeriodId: string | number | undefined | null;
  // onChange now optionally provides the selected period object as a second argument
  onChange: (id: string | number | undefined, period?: AssessmentPeriodRead) => void;
  assessmentPeriods?: AssessmentPeriodRead[]; // Assessment periods passed from parent
  isLoading?: boolean; // Loading state passed from parent
};

const AssessmentPeriodListWithStatus: React.FC<AssessmentPeriodListWithStatusProps> = ({
  selectedAssessmentPeriodId,
  onChange,
  assessmentPeriods = [],
  isLoading = false,
}) => {
  // Helper function to get status label
  const getStatusLabel = (status?: number): string => {
    if (status === undefined) return 'Unknown';
    return getAssessmentStatusLabel(status);
  };

  // Helper function to get status color
  const getStatusColor = (status?: number): string => {
    if (status === undefined) return '#757575';
    const color = getAssessmentStatusColor(status);
    // Map color names to hex values
    const colorMap: Record<string, string> = {
      grey: '#6b7280',
      green: '#10b981',
      blue: '#3b82f6',
      orange: '#f97316',
    };
    return colorMap[color] || '#9ca3af';
  };

  const options = useMemo(
    () =>
      [...assessmentPeriods]
        .sort(
          (a, b) =>
            (b.start_date_time ? new Date(b.start_date_time).getTime() : 0) -
            (a.start_date_time ? new Date(a.start_date_time).getTime() : 0)
        )
        .map(p => ({
          value: String(p.id),
          label: p.name || '',
          status: p.status,
        })),
    [assessmentPeriods]
  );

  useEffect(() => {
    if (isLoading || options.length === 0) return;

    const selectedValue = selectedAssessmentPeriodId != null 
      ? String(selectedAssessmentPeriodId) 
      : null;
    const selectedExists = options.some(o => o.value === selectedValue);
    
    if (selectedValue == null || !selectedExists) {
      // Convert string value to number for onChange
      const numericValue = parseInt(options[0].value, 10);
      const period = assessmentPeriods.find(p => String(p.id) === options[0].value);
      onChange(isNaN(numericValue) ? options[0].value : numericValue, period);
    }
  }, [isLoading, options, selectedAssessmentPeriodId, onChange]);

  const handleValueChange = (value: string) => {
    const numericValue = parseInt(value, 10);
    const period = assessmentPeriods.find(p => String(p.id) === value);
    onChange(isNaN(numericValue) ? value : numericValue, period);
  };

  const currentValue = selectedAssessmentPeriodId != null 
    ? String(selectedAssessmentPeriodId) 
    : undefined;

  const selectedOption = options.find(opt => opt.value === currentValue);

  return (
    <AssessmentPeriodListContainer>
      <Select value={currentValue} onValueChange={handleValueChange}>
        <SelectTrigger className="w-[160px]">
          <SelectValue>
            <OptionContent>
              <span>{selectedOption?.label || '-'}</span>
              {selectedOption?.status != null && (
                <StatusBadge color={getStatusColor(selectedOption.status)}>
                  {getStatusLabel(selectedOption.status)}
                </StatusBadge>
              )}
            </OptionContent>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {options.map(option => (
            <SelectItem key={option.value} value={option.value}>
              <OptionContent>
                <span>{option.label}</span>
                {option.status != null && (
                  <StatusBadge color={getStatusColor(option.status)}>
                    {getStatusLabel(option.status)}
                  </StatusBadge>
                )}
              </OptionContent>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </AssessmentPeriodListContainer>
  );
};

export default AssessmentPeriodListWithStatus;






